import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ActionType, ResourceType } from '../../types/rbac';
import { RoleService } from '../../use-cases/rbac/role-service';
import { useAuth } from '../auth';

interface RBACContextType {
  permissions: { resource: string; action: string }[];
  userRoles: string[];
  loading: boolean;
  hasPermission: (resource: ResourceType, action: ActionType) => boolean;
  refreshPermissions: () => Promise<void>;
}

const RBACContext = createContext<RBACContextType | null>(null);

interface RBACProviderProps {
  children: ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<
    { resource: string; action: string }[]
  >([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const loadUserPermissions = useCallback(async () => {
    if (!user || !user.roles || user.roles.length === 0) {
      setPermissions([]);
      setUserRoles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const roleService = new RoleService();

      // Obtener permisos del usuario basado en sus roles
      const userPermissions = await roleService.getUserPermissions(user.roles);
      setPermissions(userPermissions);
      setUserRoles(user.roles);
    } catch (error) {
      console.error('Error al cargar permisos de usuario:', error);
      setPermissions([]);
      setUserRoles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const hasPermission = (
    resource: ResourceType,
    action: ActionType,
  ): boolean => {
    if (loading) return false;

    return permissions.some(
      (permission) =>
        permission.resource === resource && permission.action === action,
    );
  };

  const refreshPermissions = async () => {
    await loadUserPermissions();
  };

  useEffect(() => {
    loadUserPermissions();
  }, [loadUserPermissions]);

  const contextValue: RBACContextType = {
    permissions,
    userRoles,
    loading,
    hasPermission,
    refreshPermissions,
  };

  return (
    <RBACContext.Provider value={contextValue}>{children}</RBACContext.Provider>
  );
};

export const useRBACContext = (): RBACContextType => {
  const context = useContext(RBACContext);

  if (!context) {
    throw new Error('useRBACContext debe ser usado dentro de un RBACProvider');
  }

  return context;
};

// Hook personalizado para verificar permisos específicos
export const usePermission = (
  resource: ResourceType,
  action: ActionType,
): boolean => {
  const { hasPermission } = useRBACContext();
  return hasPermission(resource, action);
};

// Hook para verificar múltiples permisos
export const usePermissions = (
  requiredPermissions: { resource: ResourceType; action: ActionType }[],
): boolean => {
  const { hasPermission } = useRBACContext();

  return requiredPermissions.every(({ resource, action }) =>
    hasPermission(resource, action),
  );
};

// Hook para verificar si el usuario es super administrador
// (definido como quien puede gestionar roles o usuarios admin)
export const useIsSuperAdmin = (): boolean => {
  const { hasPermission } = useRBACContext();

  return (
    hasPermission('roles', 'manage') || hasPermission('admin_users', 'manage')
  );
};
